import React, { Component } from "react";
import moment from "moment";
import Select from "react-select";
import {
  AutoSizer,
  List,
  CellMeasurer,
  CellMeasurerCache,
} from "react-virtualized";
import { connect } from "react-redux";

import {
  getRateRows,
  createRatesCsv,
  RATE_DISCLOSURE,
} from "../utils/pricingBands";
import Loading from "./../components/popups/loading.jsx";
import MerchantRateRow from "./../components/bulk/merchantRateRow.jsx";
import {
  sortNumsAsc,
  sortNumsDesc,
  sortAlphaAsc,
  sortAlphaDesc,
} from "./../utils/sort";
import { defaultAction } from "./../actions/defaultAction.js";
import * as ActionTypes from "./../constants/actionTypes";
import { score } from "ccui-framework";
import { HeadLine } from "../modules/HeadLine";
import styled from "@emotion/styled";

const DownloadLink = styled.button`
  background: #ffffff;
  border-radius: 80px;
  font-family: Montserrat, serif;
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  text-align: center;
  color: #c7a878;
  border: 1px solid rgba(199, 168, 120, 0.32);
  height: 40px;
  width: 200px;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    color: #c7a878;
    text-decoration: none;
    transform: translateY(-0.5px);
    border-color: #b37b28;
  }
`;

const columns = [
  ["name", "Brand"],
  ["percentage", "Payout"],
  ["balance", "Card Balance"],
  ["submission_method", "Submission Method"],
];

class VirtualRateList extends Component {
  cache = new CellMeasurerCache({ fixedWidth: true, defaultHeight: 60 });

  componentDidUpdate(previousProps) {
    if (previousProps.rows !== this.props.rows) {
      this.cache.clearAll();
      if (this.list) {
        this.list.recomputeRowHeights();
        this.list.scrollToPosition(0);
      }
    }
  }

  render() {
    const { rows, width, height, onScrollbarPresenceChange } = this.props;
    return (
      <List
        ref={(list) => {
          this.list = list;
        }}
        width={width}
        height={height}
        rowCount={rows.length}
        rowHeight={this.cache.rowHeight}
        deferredMeasurementCache={this.cache}
        overscanRowCount={5}
        onScrollbarPresenceChange={onScrollbarPresenceChange}
        role="rowgroup"
        containerRole="presentation"
        aria-label="Scroll to browse rates"
        rowRenderer={({ index, key, parent, style }) => (
          <CellMeasurer
            key={key}
            cache={this.cache}
            parent={parent}
            columnIndex={0}
            rowIndex={index}
          >
            {({ registerChild }) => (
              <div
                ref={registerChild}
                style={style}
                role="presentation"
                className="rates-virtual-row"
              >
                <MerchantRateRow {...rows[index]} rowIndex={index + 2} />
              </div>
            )}
          </CellMeasurer>
        )}
      />
    );
  }
}

class CurrentRates extends Component {
  state = { scrollbarWidth: 0 };

  updateScrollbarWidth = ({ size }) => {
    if (size !== this.state.scrollbarWidth) {
      this.setState({ scrollbarWidth: size });
    }
  };

  componentDidMount() {
    const { setSortOrder, setFieldToSort } = this.props;
    setSortOrder("asc");
    setFieldToSort("name");
  }

  componentWillUnmount() {
    const { clearSearchText, setSortOrder } = this.props;
    clearSearchText();
    setSortOrder("desc");
  }
  isSortableField = (fieldToSort) => {
    const { userActions } = this.props;
    const caretClasses =
      userActions.get("sortOrder") === "asc"
        ? "fa fa-lg fa-caret-up mar-l-10"
        : "fa fa-lg fa-caret-down mar-l-10";
    return (
      fieldToSort === userActions.get("fieldToSort") && (
        <i aria-hidden="true" className={caretClasses}></i>
      )
    );
  };
  setSortOrderAndSortField = (fieldToSort) => {
    const { setSortOrder, setFieldToSort, userActions } = this.props;
    setFieldToSort(fieldToSort);
    setSortOrder(
      userActions.get("fieldToSort") === fieldToSort &&
        userActions.get("sortOrder") === "asc"
        ? "desc"
        : "asc"
    );
  };
  sortRows = (a, b) => {
    const { userActions } = this.props;
    const sortOrder = userActions.get("sortOrder");

    switch (userActions.get("fieldToSort")) {
      case "name":
        return sortOrder === "asc"
          ? sortAlphaAsc(a.name, b.name)
          : sortAlphaDesc(a.name, b.name);
      case "percentage":
        return sortOrder === "asc"
          ? sortNumsAsc(a.sellPercentage, b.sellPercentage)
          : sortNumsDesc(a.sellPercentage, b.sellPercentage);
      case "balance":
        return sortOrder === "asc"
          ? sortNumsAsc(a.band.min, b.band.min)
          : sortNumsDesc(a.band.min, b.band.min);
      case "submission_method":
        return sortOrder === "asc"
          ? sortAlphaAsc(a.submissionMethod, b.submissionMethod)
          : sortAlphaDesc(a.submissionMethod, b.submissionMethod);
      default:
        return sortOrder === "asc"
          ? sortNumsAsc(a.name, b.name)
          : sortNumsDesc(a.name, b.name);
    }
  };

  filterBySearch = ({ name, aliases, band, submissionMethod }) => {
    const search = this.props.userActions.get("searchText");
    return (
      score({ name, aliases }, search, -1500) ||
      `${band.percentage}% ${band.min} ${band.max} ${submissionMethod}`
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  };

  downloadCsv = () => {
    const csv = createRatesCsv(this.props.merchantsSell, {
      retrievedAt: this.props.ratesRetrievedAt,
    });
    const url = URL.createObjectURL(
      new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8;" })
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `current_rates_${moment().format(
      "YYYY-MM-DD_HH-mm-ss"
    )}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  render() {
    const { merchantsSell, userInfo, updateSearchText, ratesRetrievedAt } =
      this.props;

    if (userInfo.get("displayLoading") || !merchantsSell.length) {
      return <Loading showLoading />;
    }

    const filteredRows = getRateRows(merchantsSell)
      .filter(this.filterBySearch)
      .sort(
        (a, b) =>
          this.sortRows(a, b) ||
          a.name.localeCompare(b.name) ||
          a.band.min - b.band.min
      );
    const { userActions } = this.props;

    return (
      <div className="current-rates-container">
        <div className="rates-heading">
          <div>
            <HeadLine headingText="Rate Sheet" />
            <p className="rates-timestamp">
              {ratesRetrievedAt ? (
                <>
                  Rates retrieved{" "}
                  <time dateTime={new Date(ratesRetrievedAt).toISOString()}>
                    {new Date(ratesRetrievedAt).toLocaleString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                      hour: "numeric",
                      minute: "2-digit",
                      timeZoneName: "short",
                    })}
                  </time>
                  <span aria-hidden="true"> · </span>
                </>
              ) : null}
              {RATE_DISCLOSURE}
            </p>
          </div>
          <div className="rates-actions">
            <div className="rates-search">
              <i aria-hidden="true" className="fa fa-search" />
              <input
                type="search"
                aria-label="Search brands"
                placeholder="Search brands..."
                onChange={updateSearchText}
              />
            </div>
            <DownloadLink
              type="button"
              onClick={this.downloadCsv}
              title="Download payouts for all brands and card balances."
            >
              Download CSV
            </DownloadLink>
          </div>
        </div>
        <div className="rates-mobile-sort">
          <label htmlFor="rates-sort">Sort by</label>
          <Select
            inputId="rates-sort"
            className="rates-sort-select"
            classNamePrefix="rates-sort"
            isSearchable={false}
            menuPlacement="bottom"
            menuPosition="absolute"
            value={columns
              .map(([value, label]) => ({ value, label }))
              .find(
                (option) => option.value === userActions.get("fieldToSort")
              )}
            options={columns.map(([value, label]) => ({ value, label }))}
            onChange={(option) => this.setSortOrderAndSortField(option.value)}
            styles={{
              control: (base, state) => ({
                ...base,
                minHeight: 44,
                borderColor: state.isFocused ? "#ad9269" : "#d8d8d8",
                boxShadow: state.isFocused ? "0 0 0 1px #ad9269" : "none",
                "&:hover": { borderColor: "#ad9269" },
              }),
              menu: (base) => ({ ...base, zIndex: 20 }),
              option: (base, state) => ({
                ...base,
                minHeight: 44,
                display: "flex",
                alignItems: "center",
                color: "#1f2021",
                backgroundColor: state.isSelected
                  ? "#e8dcc9"
                  : state.isFocused
                  ? "#f6f4f0"
                  : "white",
                ":active": { backgroundColor: "#e8dcc9" },
              }),
            }}
          />
          <button
            type="button"
            onClick={() =>
              this.setSortOrderAndSortField(userActions.get("fieldToSort"))
            }
          >
            {userActions.get("sortOrder") === "asc"
              ? "Ascending"
              : "Descending"}
          </button>
        </div>
        <div
          className="rates-table"
          role="table"
          aria-label="Current rates"
          aria-colcount={4}
          aria-rowcount={filteredRows.length + 1}
        >
          <div
            role="rowgroup"
            className="rates-table-heading"
            style={{ paddingRight: this.state.scrollbarWidth }}
          >
            <div role="row" className="rates-row" aria-rowindex={1}>
              {columns.map(([field, label]) => (
                <div
                  role="columnheader"
                  key={field}
                  aria-sort={
                    userActions.get("fieldToSort") === field
                      ? userActions.get("sortOrder") === "asc"
                        ? "ascending"
                        : "descending"
                      : "none"
                  }
                >
                  <button
                    type="button"
                    onClick={() => this.setSortOrderAndSortField(field)}
                  >
                    {label} {this.isSortableField(field)}
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div className="rates-list">
            {filteredRows.length ? (
              <AutoSizer>
                {({ width, height }) => (
                  <VirtualRateList
                    key={width}
                    width={width}
                    height={height}
                    rows={filteredRows}
                    onScrollbarPresenceChange={this.updateScrollbarWidth}
                  />
                )}
              </AutoSizer>
            ) : (
              <div className="rates-empty" role="row">
                <div role="cell" aria-colspan={4}>
                  No Results Found
                </div>
              </div>
            )}
          </div>
        </div>
        <p className="rates-results" role="status">
          {filteredRows.length.toLocaleString()}{" "}
          {filteredRows.length === 1 ? "rate" : "rates"}
          {userActions.get("searchText")
            ? " matching your search"
            : " available"}
        </p>
      </div>
    );
  }
}

export default connect(
  (state) => ({
    merchantsSell: state.merchants,
    ratesRetrievedAt: state.ratesRetrievedAt,
    userInfo: state.userInfo,
    userActions: state.userActions,
    customerInfo: state.customerInfo,
  }),
  (dispatch) => ({
    updateSearchText: (e) =>
      dispatch(
        defaultAction(ActionTypes.SET_SEARCH_TEXT, e.target.value.trim())
      ),
    clearSearchText: () =>
      dispatch(defaultAction(ActionTypes.RESET_SEARCH_TEXT)),
    setFieldToSort: (v) =>
      dispatch(defaultAction(ActionTypes.SET_FIELD_TO_SORT, v)),
    setSortOrder: (v) => dispatch(defaultAction(ActionTypes.SET_SORT_ORDER, v)),
  })
)(CurrentRates);
